import type { HttpService } from '@storebrand-digital/bank-foundation-http-service';

import type { BankAccount } from '../../app/types';
import ApiRoutePath from '../constants/apiRoutePath';
import { type AccountType } from '../enums';
import type {
  AccessToForeignPaymentsPayload,
  ChangeBankPayload,
  CloseBankAccountsPayload,
  CloseBSUAccountPayload,
  GetBankAccountsForSharedAccountsOrFeeDeductionReponse,
  GetBankAccountsReponse,
  GetCountriesResponse,
  GetCustomerBankAccountsResponse,
  GetFixedRateDepositAccountsResponse,
  OrderAccountStatementsPayload,
  OrderGoodnessCertificateOrCustomerDeclarationPayload,
  OrderPumPayload,
  PostFilesResponse,
  RenewFixedDepositAccountsPayload,
  SharedAccountWithCardPayload,
  ValidateBankAccountPayload,
  ValidateBankAccountResponse,
} from '../types';

export default class OnlineBankService {
  #httpService: HttpService;

  constructor(httpService: HttpService) {
    this.#httpService = httpService;
  }

  validateBankAccountBelongsToCustomer = async (
    payload: ValidateBankAccountPayload
  ) =>
    this.#httpService
      .post<
        ValidateBankAccountResponse,
        ValidateBankAccountPayload
      >(ApiRoutePath.POST_VALIDATE_BANK_ACCOUNT, payload)
      .then(({ data }) => data);

  getClosableBankAccounts = async () =>
    this.#httpService
      .get<GetBankAccountsReponse>(ApiRoutePath.GET_BANK_ACCOUNTS)
      .then(({ data }) => data);

  closeBankAccounts = async (payload: CloseBankAccountsPayload) =>
    this.#httpService
      .post(ApiRoutePath.POST_CLOSE_BANK_ACCOUNTS, payload)
      .then((res) => res);

  getCountries = async () =>
    this.#httpService
      .get<GetCountriesResponse>(ApiRoutePath.GET_COUNTRIES)
      .then(({ data }) => data);

  getHasEnabledForeignPayments = async () =>
    this.#httpService
      .get<boolean>(ApiRoutePath.GET_HAS_ACCESS_TO_FOREIGN_PAYMENTS)
      .then(({ data }) => data);

  postAccessToForeignPayments = async (
    payload: AccessToForeignPaymentsPayload
  ) =>
    this.#httpService
      .post(ApiRoutePath.POST_ACCESS_TO_FOREIGN_PAYMENTS, payload)
      .then((res) => res);

  getBankAccountsForSharedAccountOrFeeDeduction = async () =>
    this.#httpService
      .get<GetBankAccountsForSharedAccountsOrFeeDeductionReponse>(
        ApiRoutePath.GET_BANK_ACCOUNTS_FOR_SHARED_ACCOUNT
      )
      .then(({ data }) => data);

  postSharedAccountsWithCard = async (payload: SharedAccountWithCardPayload) =>
    this.#httpService
      .post(ApiRoutePath.POST_SHARED_ACCPUNTS_WITH_CARD, payload)
      .then((res) => res);

  postOrderPUM = async (payload: OrderPumPayload) =>
    this.#httpService
      .post(ApiRoutePath.POST_ORDER_PUM, payload)
      .then((res) => res);

  getFixedRateDepositAccounts = async () =>
    this.#httpService
      .get<GetFixedRateDepositAccountsResponse>(
        ApiRoutePath.GET_FIXED_RATE_DEPOSIT_ACCOUNTS
      )
      .then(({ data }) => data);

  postRenewFixedRateDepositAccounts = async (
    payload: RenewFixedDepositAccountsPayload
  ) =>
    this.#httpService
      .post(ApiRoutePath.POST_RENEW_FIXED_RATE_DEPOSIT_ACCOUNTS, payload)
      .then((res) => res);

  getCustomerBankAccounts = async ({
    includeInactiveAccounts,
    accountType,
  }: {
    accountType?: AccountType;
    includeInactiveAccounts?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (accountType) {
      searchParams.append('accountType', accountType);
    }
    if (includeInactiveAccounts) {
      searchParams.append('includeInactiveAccounts', 'true');
    }

    return this.#httpService
      .get<GetCustomerBankAccountsResponse>(
        `${ApiRoutePath.GET_CUSTOMER_BANK_ACCOUNTS}?${searchParams.toString()}`
      )
      .then(({ data }) => data);
  };

  postOrderAccountStatements = async (payload: OrderAccountStatementsPayload) =>
    this.#httpService
      .post(ApiRoutePath.POST_ORDER_ACCOUNT_STATEMENTS, payload)
      .then((res) => res);

  postOrderGoodnessCertificateOrCustomerDeclaration = async (
    payload: OrderGoodnessCertificateOrCustomerDeclarationPayload
  ) =>
    this.#httpService.post(
      ApiRoutePath.POST_ORDER_GOODNESS_CERTIFICATE_OR_CUSTOMER_DECLARTION,
      payload
    );

  postChangeBank = async (payload: ChangeBankPayload) =>
    this.#httpService.post(ApiRoutePath.POST_ORDER_BANK_SWITCH, payload);

  getBSUAccount = async () =>
    this.#httpService.get<BankAccount>(ApiRoutePath.GET_BSU_ACCOUNT);

  postCloseBSUAccount = async (payload: CloseBSUAccountPayload) =>
    this.#httpService.post(ApiRoutePath.POST_CLOSE_BSU_ACCOUNT, payload);

  postSecureDocuments = async (payload: FormData) =>
    this.#httpService.post<PostFilesResponse, FormData>(
      ApiRoutePath.POST_DOCUMENTS,
      payload,
      { headers: { secure: 'true' } }
    );
}
